import * as React from 'react';
import { Outlet } from 'react-router';
import { HexagonIcon, LogInIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Header } from '@/components/layout/header';
import { Sidebar } from '@/components/layout/sidebar';
import { useSession } from '@/components/auth/user-provider';
import { useStaffSession } from '@/components/auth/staff-session-provider';
import { Navigate } from 'react-router';
import { safeGetItem, safeSetItem } from '@/lib/safe-storage';

const SIDEBAR_STORAGE_KEY = 'cdf-sidebar-collapsed';

/** Shown while MSAL finishes startup / redirect handling. */
function SessionLoadingScreen() {
  return (
    <div className="text-muted-foreground flex min-h-svh items-center justify-center text-sm">
      Checking sign-in…
    </div>
  );
}

/** Shown when there is no active session. Replace with a real IdP redirect. */
function SignedOutScreen() {
  const { signIn } = useSession();
  const [busy, setBusy] = React.useState(false);

  const handleSignIn = () => {
    if (busy) return;
    setBusy(true);
    try {
      // Synchronous open kept on the user-gesture stack for iframe → top-level handoff.
      signIn();
    } finally {
      // Keep the button disabled briefly so double-clicks cannot start
      // overlapping MSAL interactions (a common freezes/crash cause).
      window.setTimeout(() => setBusy(false), 1500);
    }
  };

  return (
    <div className="bg-background flex min-h-svh items-center justify-center p-4">
      <Card className="w-full max-w-sm">
        <CardHeader className="items-center text-center">
          <HexagonIcon className="mx-auto size-8" aria-hidden />
          <CardTitle>Foundry Template</CardTitle>
          <CardDescription>Sign in to continue.</CardDescription>
        </CardHeader>
        <CardContent>
          <Button className="w-full" onClick={handleSignIn} disabled={busy}>
            <LogInIcon /> {busy ? 'Opening sign-in…' : 'Sign in'}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

export function AppShell() {
  const { session } = useSession();
  const { staffRole, loading: staffLoading } = useStaffSession();
  const [collapsed, setCollapsed] = React.useState(
    () => safeGetItem(SIDEBAR_STORAGE_KEY) === 'true'
  );
  const [mobileSidebarOpen, setMobileSidebarOpen] = React.useState(false);

  const toggleSidebar = () => {
    setCollapsed((value) => {
      safeSetItem(SIDEBAR_STORAGE_KEY, String(!value));
      return !value;
    });
  };

  if (session.status === 'loading' || staffLoading) {
    return <SessionLoadingScreen />;
  }

  if (session.status === 'signedOut') {
    return <SignedOutScreen />;
  }

  // Redirect community/non-staff users to the public site.
  if (staffRole !== 'staff' && staffRole !== 'admin' && staffRole !== 'super_admin') {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="bg-background flex h-svh flex-col">
      {/* Skip link for keyboard users (WCAG 2.4.1) — revealed on focus */}
      <a
        href="#main-content"
        className="bg-background text-foreground ring-ring sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:rounded-md focus:px-3 focus:py-2 focus:text-sm focus:ring-2"
      >
        Skip to main content
      </a>
      {/* Full-width sticky header — stays put while the content scrolls */}
      <Header onMobileSidebarOpen={() => setMobileSidebarOpen(true)} />
      <div className="flex min-h-0 flex-1">
        {/* Sidebar: desktop inline panel, mobile slide-in overlay */}
        <Sidebar
          collapsed={collapsed}
          onToggle={toggleSidebar}
          mobileOpen={mobileSidebarOpen}
          onMobileClose={() => setMobileSidebarOpen(false)}
        />
        <main
          id="main-content"
          tabIndex={-1}
          className="flex min-w-0 flex-1 flex-col gap-4 overflow-y-auto p-4 outline-none md:p-6"
        >
          <Outlet />
        </main>
      </div>
    </div>
  );
}
